/**
 * Pure helpers for the account-wide Envelopes page: quick-view chips, search,
 * "last change" ordering, and the row shape shared by both data modes.
 */
import { getEnvelopeListSubject } from '../components/envelopes/envelopeFormatUtils';
import { bouncedSignersOf } from '../components/envelopes/envelopeRecipientAlerts';

export const ENVELOPES_SCOPE_MODE_KEY = 'envelopes.scopeMode';
export const ENVELOPES_SCOPE_ALL = 'all';
export const ENVELOPES_SCOPE_CLIENT = 'client';

/**
 * Quick views mirroring DocuSign's left rail, keyed on AdviceLink statuses.
 * `orBounced` also takes in-flight envelopes with a signer whose email bounced.
 */
export const ENVELOPE_VIEW_CHIPS = [
  { id: 'all', label: 'All', statuses: null },
  { id: 'draft', label: 'Drafts', statuses: ['draft'] },
  { id: 'tagging', label: 'In progress', statuses: ['tagging'] },
  { id: 'sent', label: 'Waiting on signature', statuses: ['sent'] },
  { id: 'attention', label: 'Needs attention', statuses: ['declined', 'voided'], orBounced: true },
  { id: 'completed', label: 'Completed', statuses: ['completed'] },
];

export function chipMatches(chipId, row) {
  const chip = ENVELOPE_VIEW_CHIPS.find((c) => c.id === chipId) || ENVELOPE_VIEW_CHIPS[0];
  if (!chip.statuses) return true;
  if (chip.statuses.includes(row?.status)) return true;
  return Boolean(chip.orBounced) && bouncedSignersOf(row).length > 0;
}

export function countByChip(rows) {
  const counts = {};
  ENVELOPE_VIEW_CHIPS.forEach((chip) => {
    counts[chip.id] = (rows || []).filter((row) => chipMatches(chip.id, row)).length;
  });
  return counts;
}

function toMillis(value) {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** ISO timestamp of the most recent lifecycle change — the table's default sort. */
export function envelopeLastChangeAt(row) {
  if (!row) return null;
  const candidates = [row.lastModifiedAt, row.completedAt, row.sentAt, row.savedAt, row.createdAt];
  let best = null;
  candidates.forEach((value) => {
    if (value && toMillis(value) > toMillis(best)) best = value;
  });
  return best;
}

export function recipientNamesOf(row) {
  if (Array.isArray(row?.recipientNames) && row.recipientNames.length) return row.recipientNames;
  const recipients = Array.isArray(row?.recipients) ? row.recipients : [];
  const signers = recipients.filter((r) => r && r.deliveryRole !== 'carbonCopy' && r.role !== 'CC');
  return signers.map((r) => r.name || r.email).filter(Boolean);
}

export function buildEnvelopeSearchText(row) {
  if (!row) return '';
  let subject = row.emailSubject || '';
  if (row.source !== 'docusign') {
    try {
      subject = getEnvelopeListSubject(row, row.firmName || '', { clientName: row.clientName || '' }) || subject;
    } catch {
      // keep stored subject
    }
  }
  const recipients = Array.isArray(row.recipients) ? row.recipients : [];
  return [
    row.name,
    subject,
    row.clientName,
    row.clientEmail,
    row.firmName,
    row.createdBy,
    row.createdByName,
    row.docusignSenderName,
    row.docusignEnvelopeId,
    ...recipients.map((r) => `${r?.name || ''} ${r?.email || ''}`),
    ...(Array.isArray(row.recipientNames) ? row.recipientNames : []),
  ].filter(Boolean).join(' ').toLowerCase();
}

export function filterEnvelopeRows(rows, { search = '', chip = 'all' } = {}) {
  const q = String(search || '').trim().toLowerCase();
  return (rows || []).filter((row) => {
    if (!chipMatches(chip, row)) return false;
    if (!q) return true;
    return buildEnvelopeSearchText(row).includes(q);
  });
}

function clientNameOf(client) {
  const personal = client?.personal || {};
  if (personal.firstName || personal.surname) {
    return [personal.firstName, personal.surname].filter(Boolean).join(' ');
  }
  return personal.email || 'Client';
}

/**
 * Shape a per-client envelope (from /api/clients/:id/envelopes) like an
 * account row so "Selected client" mode shares the table code path.
 */
export function normalizeClientEnvelopeRow(envelope, client, { firmName = '' } = {}) {
  if (!envelope) return null;
  const saved = envelope.savedSignedDocs || {};
  return {
    ...envelope,
    source: 'advicelink',
    clientId: client?.id || null,
    clientName: clientNameOf(client),
    clientEmail: client?.personal?.email || null,
    advisoryTeamId: envelope.advisoryTeamId || client?.assignment?.assignedAdvisory || null,
    firmName: firmName || null,
    createdByName: envelope.createdByName || null,
    clientFlags: client ? {
      isArchived: Boolean(client.isArchived),
      isTestClient: Boolean(client.isTestClient),
      isLost: Boolean(client.isLost),
      isDnq: Boolean(client.isDnq),
      unreleased: false,
    } : null,
    signedDocumentId: saved.combinedSigned || envelope.savedDocumentId || null,
  };
}

export function readEnvelopesScopeMode() {
  try {
    const stored = sessionStorage.getItem(ENVELOPES_SCOPE_MODE_KEY);
    return stored === ENVELOPES_SCOPE_CLIENT ? ENVELOPES_SCOPE_CLIENT : ENVELOPES_SCOPE_ALL;
  } catch {
    return ENVELOPES_SCOPE_ALL;
  }
}

export function writeEnvelopesScopeMode(mode) {
  try {
    if (mode === ENVELOPES_SCOPE_CLIENT) sessionStorage.setItem(ENVELOPES_SCOPE_MODE_KEY, mode);
    else sessionStorage.removeItem(ENVELOPES_SCOPE_MODE_KEY);
  } catch {
    // ignore storage errors
  }
}

export function isEnvelopeInFlight(row) {
  return row?.status === 'tagging' || row?.status === 'sent';
}
